'use client';

import {
  RoomAudioRenderer,
  StartAudio,
  VideoTrack,
  useChat,
  useParticipants,
} from '@livekit/components-react';
import {
  type Participant,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  Room,
  RoomEvent,
  Track,
} from 'livekit-client';
import NextImage from 'next/image';
import { QRCodeSVG } from 'qrcode.react';
import React from 'react';
import {
  BROADCAST_FRAME_RATE_OPTIONS,
  BROADCAST_QUALITY_OPTIONS,
  DEFAULT_BROADCAST_FRAME_RATE,
  DEFAULT_BROADCAST_QUALITY,
  DEFAULT_NOISE_SUPPRESSION_LEVEL,
  getNoiseSuppressionOption,
  NOISE_SUPPRESSION_OPTIONS,
  type BroadcastFrameRate,
  type BroadcastQuality,
  type NoiseSuppressionLevel,
} from '@/lib/broadcast-settings';
import { createViewerLink } from '@/lib/client-utils';
import {
  COLOR_BALANCE_MAX,
  COLOR_BALANCE_MIN,
  DEFAULT_COLOR_BALANCE,
  JEWELRY_FILTERS,
  type ColorBalance,
  type JewelryFilterId,
} from '@/lib/client/jewelry-video-filter';
import {
  DEFAULT_LIVE_PROFILE,
  parseLiveProfileMetadata,
  type LiveProfile,
} from '@/lib/live-profile';
import {
  DEFAULT_VIRTUAL_VIEWERS,
  getDisplayedViewerCount,
  MAX_VIRTUAL_VIEWERS,
  parseVirtualViewerMetadata,
  type VirtualViewerSettings,
} from '@/lib/virtual-viewers';
import styles from '@/styles/Live.module.css';

const STABLE_PLAYOUT_DELAY_SECONDS = 3;
const VIDEO_FRAME_RECOVERY_DELAY_MS = 7000;

type LiveStageProps = {
  room: Room;
  role: 'host' | 'viewer';
  hostIdentity: string;
  liveProfile?: LiveProfile;
  onClose: () => void;
  cameraEnabled?: boolean;
  cameraFacingMode?: 'user' | 'environment';
  cameraSwitching?: boolean;
  videoFilter?: JewelryFilterId;
  filterSwitching?: boolean;
  beautyEnabled?: boolean;
  beautySwitching?: boolean;
  colorBalance?: ColorBalance;
  virtualViewers?: VirtualViewerSettings;
  virtualViewerSaving?: boolean;
  microphoneEnabled?: boolean;
  noiseSuppressionLevel?: NoiseSuppressionLevel;
  noiseSuppressionSwitching?: boolean;
  broadcastQuality?: BroadcastQuality;
  qualitySwitching?: boolean;
  broadcastFrameRate?: BroadcastFrameRate;
  frameRateSwitching?: boolean;
  onToggleCamera?: () => void;
  onSwitchCamera?: () => void;
  onSelectVideoFilter?: (filterId: JewelryFilterId) => void | Promise<void>;
  onToggleBeauty?: () => void;
  onColorBalanceChange?: (balance: ColorBalance) => void;
  onResetColorBalance?: () => void;
  onSaveVirtualViewers?: (
    settings: Pick<VirtualViewerSettings, 'base' | 'fluctuation'>,
  ) => Promise<void>;
  onToggleMicrophone?: () => void;
  onSelectNoiseSuppression?: (level: NoiseSuppressionLevel) => void | Promise<void>;
  onSelectBroadcastQuality?: (quality: BroadcastQuality) => void | Promise<void>;
  onSelectBroadcastFrameRate?: (frameRate: BroadcastFrameRate) => void | Promise<void>;
};

export function LiveStage({
  room,
  role,
  hostIdentity,
  liveProfile: initialLiveProfile = DEFAULT_LIVE_PROFILE,
  onClose,
  cameraEnabled = true,
  cameraFacingMode = 'user',
  cameraSwitching = false,
  videoFilter = 'off',
  filterSwitching = false,
  beautyEnabled = false,
  beautySwitching = false,
  colorBalance = DEFAULT_COLOR_BALANCE,
  virtualViewers = DEFAULT_VIRTUAL_VIEWERS,
  virtualViewerSaving = false,
  microphoneEnabled = true,
  noiseSuppressionLevel = DEFAULT_NOISE_SUPPRESSION_LEVEL,
  noiseSuppressionSwitching = false,
  broadcastQuality = DEFAULT_BROADCAST_QUALITY,
  qualitySwitching = false,
  broadcastFrameRate = DEFAULT_BROADCAST_FRAME_RATE,
  frameRateSwitching = false,
  onToggleCamera,
  onSwitchCamera,
  onSelectVideoFilter,
  onToggleBeauty,
  onColorBalanceChange,
  onResetColorBalance,
  onSaveVirtualViewers,
  onToggleMicrophone,
  onSelectNoiseSuppression,
  onSelectBroadcastQuality,
  onSelectBroadcastFrameRate,
}: LiveStageProps) {
  const [shareOpen, setShareOpen] = React.useState(false);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [viewerSettingsOpen, setViewerSettingsOpen] = React.useState(false);
  const [noiseSettingsOpen, setNoiseSettingsOpen] = React.useState(false);
  const [qualitySettingsOpen, setQualitySettingsOpen] = React.useState(false);
  const [frameRateSettingsOpen, setFrameRateSettingsOpen] = React.useState(false);
  const [participantListOpen, setParticipantListOpen] = React.useState(false);
  const [viewerLink, setViewerLink] = React.useState('');
  const videoElementRef = React.useRef<HTMLVideoElement>(null);
  const videoRecoveryAttemptsRef = React.useRef(new Map<string, number>());
  const delayedTracksRef = React.useRef(new WeakSet<RemoteTrack>());
  const [, refreshCamera] = React.useReducer((value: number) => value + 1, 0);
  const participants = useParticipants();
  const host = participants.find((participant) => participant.identity === hostIdentity);
  const liveProfile = parseLiveProfileMetadata(host?.metadata, initialLiveProfile);
  const hostCameraPublication = host?.getTrackPublication(Track.Source.Camera);
  const hostCamera =
    host && hostCameraPublication
      ? {
          participant: host,
          publication: hostCameraPublication,
          source: Track.Source.Camera,
        }
      : undefined;
  const hostMicrophone = host?.getTrackPublication(Track.Source.Microphone);
  const isLive = Boolean(host && (hostCamera || hostMicrophone));
  const viewerCount = participants.filter(
    (participant) => participant.identity !== hostIdentity,
  ).length;
  const syncedVirtualViewers = parseVirtualViewerMetadata(host?.metadata);
  const displayedVirtualViewers =
    role === 'host' ? virtualViewers.current : syncedVirtualViewers.current;
  const displayedViewerCount = getDisplayedViewerCount(viewerCount, displayedVirtualViewers);

  React.useEffect(() => {
    const updateCamera = () => refreshCamera();
    room.on(RoomEvent.TrackPublished, updateCamera);
    room.on(RoomEvent.TrackSubscribed, updateCamera);
    room.on(RoomEvent.TrackUnsubscribed, updateCamera);
    room.on(RoomEvent.TrackUnpublished, updateCamera);
    room.on(RoomEvent.TrackMuted, updateCamera);
    room.on(RoomEvent.TrackUnmuted, updateCamera);
    room.on(RoomEvent.TrackStreamStateChanged, updateCamera);
    room.on(RoomEvent.Reconnected, updateCamera);
    room.on(RoomEvent.LocalTrackPublished, updateCamera);
    room.on(RoomEvent.LocalTrackUnpublished, updateCamera);
    room.on(RoomEvent.ParticipantMetadataChanged, updateCamera);

    return () => {
      room.off(RoomEvent.TrackPublished, updateCamera);
      room.off(RoomEvent.TrackSubscribed, updateCamera);
      room.off(RoomEvent.TrackUnsubscribed, updateCamera);
      room.off(RoomEvent.TrackUnpublished, updateCamera);
      room.off(RoomEvent.TrackMuted, updateCamera);
      room.off(RoomEvent.TrackUnmuted, updateCamera);
      room.off(RoomEvent.TrackStreamStateChanged, updateCamera);
      room.off(RoomEvent.Reconnected, updateCamera);
      room.off(RoomEvent.LocalTrackPublished, updateCamera);
      room.off(RoomEvent.LocalTrackUnpublished, updateCamera);
      room.off(RoomEvent.ParticipantMetadataChanged, updateCamera);
    };
  }, [room]);

  React.useEffect(() => {
    if (role !== 'viewer') return;

    let retryTimers: number[] = [];
    const applyStablePlayoutDelay = (track: RemoteTrack | undefined) => {
      if (!track || delayedTracksRef.current.has(track)) return;
      delayedTracksRef.current.add(track);
      try {
        track.setPlayoutDelay(STABLE_PLAYOUT_DELAY_SECONDS);
      } catch {
        // Unsupported browsers continue with their native WebRTC jitter buffer.
      }
      const receiver = track.receiver as
        | (RTCRtpReceiver & { jitterBufferTarget?: number })
        | undefined;
      if (receiver && 'jitterBufferTarget' in receiver) {
        try {
          receiver.jitterBufferTarget = STABLE_PLAYOUT_DELAY_SECONDS * 1000;
        } catch {
          // The playout delay hint above remains the compatibility fallback.
        }
      }
    };
    const applyHostPlayoutDelay = () => {
      const participant = room.remoteParticipants.get(hostIdentity);
      applyStablePlayoutDelay(participant?.getTrackPublication(Track.Source.Camera)?.track);
      applyStablePlayoutDelay(participant?.getTrackPublication(Track.Source.Microphone)?.track);
    };
    const requestCamera = () => {
      const publication = room.remoteParticipants
        .get(hostIdentity)
        ?.getTrackPublication(Track.Source.Camera);
      if (publication instanceof RemoteTrackPublication && !publication.isMuted) {
        applyStablePlayoutDelay(publication.track);
        if (publication.track) return;
        publication.setSubscribed(true);
      }
    };
    const scheduleRetries = () => {
      retryTimers.forEach(window.clearTimeout);
      retryTimers = [0, 800, 2200, 5000].map((delay) => window.setTimeout(requestCamera, delay));
    };
    const onTrackPublished = (
      publication: RemoteTrackPublication,
      participant: { identity: string },
    ) => {
      if (participant.identity === hostIdentity && publication.source === Track.Source.Camera) {
        scheduleRetries();
      }
    };
    const onParticipantConnected = (participant: { identity: string }) => {
      if (participant.identity === hostIdentity) scheduleRetries();
    };
    const onTrackSubscribed = (
      track: RemoteTrack,
      _publication: RemoteTrackPublication,
      participant: RemoteParticipant,
    ) => {
      if (participant.identity === hostIdentity) applyStablePlayoutDelay(track);
    };
    room.on(RoomEvent.TrackPublished, onTrackPublished);
    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    room.on(RoomEvent.ParticipantConnected, onParticipantConnected);
    room.on(RoomEvent.Reconnected, scheduleRetries);
    scheduleRetries();
    applyHostPlayoutDelay();

    return () => {
      retryTimers.forEach(window.clearTimeout);
      room.off(RoomEvent.TrackPublished, onTrackPublished);
      room.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
      room.off(RoomEvent.ParticipantConnected, onParticipantConnected);
      room.off(RoomEvent.Reconnected, scheduleRetries);
    };
  }, [hostIdentity, role, room]);

  React.useEffect(() => {
    const video = videoElementRef.current;
    if (!video || !hostCameraPublication?.track) return;
    const publication = hostCameraPublication;
    const trackSid = publication.trackSid;

    const startPlayback = () => {
      video.muted = true;
      void video.play().catch(() => undefined);
    };
    const markPlaybackHealthy = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        videoRecoveryAttemptsRef.current.delete(trackSid);
      }
    };
    const resumeWhenVisible = () => {
      if (document.visibilityState === 'visible') startPlayback();
    };
    const recoverBlackVideo = window.setTimeout(() => {
      if (role !== 'viewer' || !(publication instanceof RemoteTrackPublication)) return;
      const decodedFrames = video.getVideoPlaybackQuality?.().totalVideoFrames ?? 0;
      const hasDecodedVideo =
        video.videoWidth > 0 &&
        video.videoHeight > 0 &&
        (video.readyState >= 2 || decodedFrames > 0);
      if (hasDecodedVideo) {
        videoRecoveryAttemptsRef.current.delete(trackSid);
        return;
      }

      const attempts = videoRecoveryAttemptsRef.current.get(trackSid) ?? 0;
      if (attempts >= 2 || publication.isMuted) return;
      videoRecoveryAttemptsRef.current.set(trackSid, attempts + 1);
      publication.setSubscribed(false);
      window.setTimeout(() => {
        if (!publication.isMuted) publication.setSubscribed(true);
      }, 250);
    }, VIDEO_FRAME_RECOVERY_DELAY_MS);

    startPlayback();
    video.addEventListener('loadedmetadata', startPlayback);
    video.addEventListener('canplay', startPlayback);
    video.addEventListener('playing', markPlaybackHealthy);
    document.addEventListener('visibilitychange', resumeWhenVisible);
    window.addEventListener('pageshow', startPlayback);

    return () => {
      window.clearTimeout(recoverBlackVideo);
      video.removeEventListener('loadedmetadata', startPlayback);
      video.removeEventListener('canplay', startPlayback);
      video.removeEventListener('playing', markPlaybackHealthy);
      document.removeEventListener('visibilitychange', resumeWhenVisible);
      window.removeEventListener('pageshow', startPlayback);
    };
  }, [hostCameraPublication, role]);

  const openShare = () => {
    setViewerLink(createViewerLink(window.location.origin));
    setShareOpen(true);
  };

  return (
    <main className={styles.page}>
      <section
        className={styles.stage}
        aria-label={role === 'host' ? '主播开播页面' : '直播观看页面'}
      >
        <div className={styles.videoLayer}>
          {hostCamera?.publication.track && !hostCamera.publication.isMuted ? (
            <VideoTrack
              key={`${hostCamera.publication.trackSid}:${hostCamera.publication.track.mediaStreamTrack.id}`}
              ref={videoElementRef}
              trackRef={hostCamera}
              className={`${styles.video} ${
                role === 'host' && cameraFacingMode === 'user' ? styles.localVideo : ''
              }`}
              autoPlay
              muted={role === 'host'}
              playsInline
              draggable={false}
              disablePictureInPicture
              onContextMenu={(event) => event.preventDefault()}
            />
          ) : (
            <div className={styles.ambientBackground} aria-hidden="true">
              <span className={styles.ambientOrb} />
            </div>
          )}
        </div>

        <div className={styles.topShade} />
        <div className={styles.bottomShade} />

        <header className={styles.topBar}>
          <div className={styles.hostSummary}>
            <button
              className={styles.avatar}
              type="button"
              onClick={() => setParticipantListOpen(true)}
              aria-label="查看直播间用户列表"
            >
              {liveProfile.avatarUrl ? (
                <NextImage
                  src={liveProfile.avatarUrl}
                  alt="直播间头像"
                  width={34}
                  height={34}
                  unoptimized
                  draggable={false}
                />
              ) : (
                '播'
              )}
            </button>
            <span className={styles.hostText}>
              <strong>{liveProfile.title}</strong>
              <small>{displayedViewerCount} 人正在观看</small>
            </span>
            {isLive && <span className={styles.liveBadge}>直播中</span>}
          </div>
          <button
            className={styles.closeButton}
            type="button"
            onClick={onClose}
            aria-label={role === 'host' ? '结束直播' : '退出直播间'}
          >
            <CloseIcon />
          </button>
        </header>

        {!isLive && (
          <div className={styles.offlineState}>
            <span className={styles.offlineIcon}>
              <VideoOffIcon />
            </span>
            <h1>{role === 'host' ? '摄像头已关闭' : '主播暂未开播'}</h1>
            <p>
              {role === 'host'
                ? '打开摄像头即可继续展示画面'
                : '你可以留在这里，开播后画面会自动出现'}
            </p>
          </div>
        )}

        <aside
          className={styles.sideControls}
          aria-label={role === 'host' ? '直播控制' : '观看控制'}
        >
          {role === 'host' && (
            <>
              <ControlButton
                label={microphoneEnabled ? '静音' : '开麦'}
                active={!microphoneEnabled}
                onClick={onToggleMicrophone}
                icon={microphoneEnabled ? <MicIcon /> : <MicOffIcon />}
              />
              <ControlButton
                label={
                  noiseSuppressionSwitching
                    ? '处理中'
                    : `${getNoiseSuppressionOption(noiseSuppressionLevel).label}降噪`
                }
                active={noiseSuppressionLevel !== 'browser'}
                onClick={() => setNoiseSettingsOpen(true)}
                disabled={noiseSuppressionSwitching}
                icon={<NoiseSuppressionIcon />}
              />
              <ControlButton
                label={cameraEnabled ? '关镜头' : '开镜头'}
                active={!cameraEnabled}
                onClick={onToggleCamera}
                disabled={qualitySwitching || frameRateSwitching}
                icon={cameraEnabled ? <CameraIcon /> : <VideoOffIcon />}
              />
              <ControlButton
                label={qualitySwitching ? '切换中' : broadcastQuality}
                active={broadcastQuality === '1080p' || broadcastQuality === '1440p'}
                onClick={() => setQualitySettingsOpen(true)}
                disabled={
                  qualitySwitching ||
                  frameRateSwitching ||
                  cameraSwitching ||
                  filterSwitching ||
                  beautySwitching ||
                  !cameraEnabled
                }
                icon={<QualityIcon />}
              />
              <ControlButton
                label={frameRateSwitching ? '切换中' : `${broadcastFrameRate} FPS`}
                active={broadcastFrameRate !== DEFAULT_BROADCAST_FRAME_RATE}
                onClick={() => setFrameRateSettingsOpen(true)}
                disabled={
                  frameRateSwitching ||
                  qualitySwitching ||
                  cameraSwitching ||
                  filterSwitching ||
                  beautySwitching ||
                  !cameraEnabled
                }
                icon={<FrameRateIcon />}
              />
              <ControlButton
                label="虚拟人数"
                active={virtualViewers.base > 0}
                onClick={() => setViewerSettingsOpen(true)}
                disabled={virtualViewerSaving}
                icon={<ViewerCountIcon />}
              />
              <ControlButton
                label={beautySwitching ? '处理中' : beautyEnabled ? '关美颜' : '美颜'}
                active={beautyEnabled}
                onClick={onToggleBeauty}
                disabled={
                  beautySwitching || filterSwitching || qualitySwitching || frameRateSwitching
                }
                icon={<BeautyIcon />}
              />
              <ControlButton
                label={
                  cameraSwitching ? '切换中' : cameraFacingMode === 'user' ? '切后置' : '切前置'
                }
                onClick={onSwitchCamera}
                disabled={cameraSwitching || qualitySwitching || frameRateSwitching}
                icon={<SwitchCameraIcon />}
              />
              <ControlButton
                label={
                  filterSwitching
                    ? '处理中'
                    : videoFilter === 'off'
                      ? '滤镜'
                      : JEWELRY_FILTERS.find((item) => item.id === videoFilter)?.label || '滤镜'
                }
                active={videoFilter !== 'off'}
                onClick={() => setFilterOpen(true)}
                disabled={filterSwitching || qualitySwitching || frameRateSwitching}
                icon={<FilterIcon />}
              />
            </>
          )}
          <ControlButton label="分享" onClick={openShare} icon={<ShareIcon />} />
        </aside>

        <LiveChat room={room} hostIdentity={hostIdentity} />
        <RoomAudioRenderer room={room} />
        {role === 'viewer' && (
          <StartAudio room={room} label="点击开启直播声音" className={styles.startAudio} />
        )}
        {shareOpen && viewerLink && (
          <ShareDialog viewerLink={viewerLink} onClose={() => setShareOpen(false)} />
        )}
        {participantListOpen && (
          <ParticipantListDialog
            participants={participants}
            hostIdentity={hostIdentity}
            localIdentity={room.localParticipant.identity}
            hostAvatarUrl={liveProfile.avatarUrl}
            onClose={() => setParticipantListOpen(false)}
          />
        )}
        {viewerSettingsOpen && role === 'host' && onSaveVirtualViewers && (
          <VirtualViewerDialog
            settings={virtualViewers}
            actualViewerCount={viewerCount}
            saving={virtualViewerSaving}
            onSave={onSaveVirtualViewers}
            onClose={() => setViewerSettingsOpen(false)}
          />
        )}
        {noiseSettingsOpen && role === 'host' && onSelectNoiseSuppression && (
          <BroadcastSettingDialog
            title="降噪强度"
            subtitle="强力档使用高质量模型，会增加少量设备负载"
            activeValue={noiseSuppressionLevel}
            switching={noiseSuppressionSwitching}
            options={NOISE_SUPPRESSION_OPTIONS}
            onSelect={onSelectNoiseSuppression}
            onClose={() => setNoiseSettingsOpen(false)}
          />
        )}
        {qualitySettingsOpen && role === 'host' && onSelectBroadcastQuality && (
          <BroadcastSettingDialog
            title="直播清晰度"
            subtitle="降低清晰度会平滑切换；升档时仅在采集不足时重启镜头"
            activeValue={broadcastQuality}
            switching={qualitySwitching}
            options={BROADCAST_QUALITY_OPTIONS}
            onSelect={onSelectBroadcastQuality}
            onClose={() => setQualitySettingsOpen(false)}
          />
        )}
        {frameRateSettingsOpen && role === 'host' && onSelectBroadcastFrameRate && (
          <BroadcastSettingDialog
            title="直播帧率"
            subtitle="降低帧率会平滑切换；60 FPS 需摄像头与网络支持"
            activeValue={broadcastFrameRate}
            switching={frameRateSwitching}
            options={BROADCAST_FRAME_RATE_OPTIONS}
            onSelect={onSelectBroadcastFrameRate}
            onClose={() => setFrameRateSettingsOpen(false)}
          />
        )}
        {filterOpen &&
          role === 'host' &&
          onSelectVideoFilter &&
          onColorBalanceChange &&
          onResetColorBalance && (
            <FilterDialog
              activeFilter={videoFilter}
              switching={filterSwitching || beautySwitching}
              colorBalance={colorBalance}
              onSelect={onSelectVideoFilter}
              onColorBalanceChange={onColorBalanceChange}
              onResetColorBalance={onResetColorBalance}
              onClose={() => setFilterOpen(false)}
            />
          )}
      </section>
    </main>
  );
}

function BroadcastSettingDialog<T extends string | number>({
  title,
  subtitle,
  activeValue,
  switching,
  options,
  onSelect,
  onClose,
}: {
  title: string;
  subtitle: string;
  activeValue: T;
  switching: boolean;
  options: ReadonlyArray<{ id: T; label: string; description: string }>;
  onSelect: (value: T) => void | Promise<void>;
  onClose: () => void;
}) {
  React.useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !switching) onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose, switching]);

  const select = async (value: T) => {
    await onSelect(value);
    onClose();
  };

  return (
    <div
      className={styles.filterOverlay}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !switching) onClose();
      }}
    >
      <section
        className={`${styles.filterDialog} ${styles.broadcastSettingsDialog}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className={styles.filterHeader}>
          <span>
            <strong>{title}</strong>
            <small>{subtitle}</small>
          </span>
          <button type="button" onClick={onClose} disabled={switching} aria-label={`关闭${title}`}>
            <CloseIcon />
          </button>
        </header>
        <div className={styles.broadcastSettingOptions}>
          {options.map((option) => {
            const selected = option.id === activeValue;
            return (
              <button
                className={`${styles.broadcastSettingOption} ${
                  selected ? styles.broadcastSettingOptionActive : ''
                }`}
                type="button"
                key={option.id}
                onClick={() => void select(option.id)}
                disabled={switching}
                aria-pressed={selected}
              >
                <span>
                  <strong>{option.label}</strong>
                  <small>{option.description}</small>
                </span>
                <b>{selected ? (switching ? '切换中' : '当前') : '选择'}</b>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function VirtualViewerDialog({
  settings,
  actualViewerCount,
  saving,
  onSave,
  onClose,
}: {
  settings: VirtualViewerSettings;
  actualViewerCount: number;
  saving: boolean;
  onSave: (settings: Pick<VirtualViewerSettings, 'base' | 'fluctuation'>) => Promise<void>;
  onClose: () => void;
}) {
  const [base, setBase] = React.useState(String(settings.base));
  const [fluctuation, setFluctuation] = React.useState(String(settings.fluctuation));

  React.useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose, saving]);

  const previewBase = Math.round(Math.max(0, Math.min(MAX_VIRTUAL_VIEWERS, Number(base) || 0)));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const fluctuationValue = Math.round(
      Math.max(0, Math.min(previewBase, Number(fluctuation) || 0)),
    );
    try {
      await onSave({ base: previewBase, fluctuation: fluctuationValue });
      onClose();
    } catch {
      // The parent keeps the dialog open and displays the synchronization error.
    }
  };

  return (
    <div
      className={styles.filterOverlay}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <form
        className={`${styles.filterDialog} ${styles.viewerSettingsDialog}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="viewer-settings-title"
        onSubmit={submit}
      >
        <header className={styles.filterHeader}>
          <span>
            <strong id="viewer-settings-title">虚拟观看人数</strong>
            <small>设置结果会同步显示给所有观众</small>
          </span>
          <button type="button" onClick={onClose} disabled={saving} aria-label="关闭人数设置">
            <CloseIcon />
          </button>
        </header>
        <div className={styles.viewerSettingsFields}>
          <label>
            <span>虚拟人数</span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={MAX_VIRTUAL_VIEWERS}
              step={1}
              value={base}
              disabled={saving}
              onChange={(event) => {
                const nextBase = event.currentTarget.value;
                const nextMaximum = Math.round(
                  Math.max(0, Math.min(MAX_VIRTUAL_VIEWERS, Number(nextBase) || 0)),
                );
                setBase(nextBase);
                if ((Number(fluctuation) || 0) > nextMaximum) {
                  setFluctuation(String(nextMaximum));
                }
              }}
              autoFocus
            />
            <small>填 0 即关闭虚拟人数</small>
          </label>
          <label>
            <span>上下浮动阈值</span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={previewBase}
              step={1}
              value={fluctuation}
              disabled={saving}
              onChange={(event) => setFluctuation(event.currentTarget.value)}
            />
            <small>例如虚拟 10、阈值 2，会在 8–12 之间缓慢浮动</small>
          </label>
        </div>
        <div className={styles.viewerCountPreview}>
          当前预览：真实 {actualViewerCount} + 虚拟 {previewBase} ={' '}
          <strong>{getDisplayedViewerCount(actualViewerCount, previewBase)}</strong> 人
        </div>
        <button className={styles.viewerSettingsSubmit} type="submit" disabled={saving}>
          {saving ? '正在同步…' : '保存人数设置'}
        </button>
      </form>
    </div>
  );
}

function FilterDialog({
  activeFilter,
  switching,
  colorBalance,
  onSelect,
  onColorBalanceChange,
  onResetColorBalance,
  onClose,
}: {
  activeFilter: JewelryFilterId;
  switching: boolean;
  colorBalance: ColorBalance;
  onSelect: (filterId: JewelryFilterId) => void | Promise<void>;
  onColorBalanceChange: (balance: ColorBalance) => void;
  onResetColorBalance: () => void;
  onClose: () => void;
}) {
  React.useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !switching) onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose, switching]);

  return (
    <div
      className={styles.filterOverlay}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !switching) onClose();
      }}
    >
      <section
        className={styles.filterDialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="filter-dialog-title"
      >
        <header className={styles.filterHeader}>
          <span>
            <strong id="filter-dialog-title">画面效果</strong>
            <small>滤镜与三色调节会同步显示给观众</small>
          </span>
          <button type="button" onClick={onClose} disabled={switching} aria-label="关闭滤镜列表">
            <CloseIcon />
          </button>
        </header>
        <div className={styles.filterGrid}>
          {JEWELRY_FILTERS.map((option) => {
            const selected = option.id === activeFilter;
            return (
              <button
                className={`${styles.filterOption} ${selected ? styles.filterOptionActive : ''}`}
                type="button"
                key={option.id}
                onClick={() => void onSelect(option.id)}
                disabled={switching}
                aria-pressed={selected}
              >
                <span className={`${styles.filterPreview} ${styles[option.previewClass]}`}>
                  <i />
                </span>
                <strong>{option.label}</strong>
                <small>{option.description}</small>
                {selected && <b>{switching ? '应用中' : '已选择'}</b>}
              </button>
            );
          })}
        </div>
        <div className={styles.colorBalancePanel}>
          <div className={styles.colorBalanceHeader}>
            <span>
              <strong>三色调节</strong>
              <small>分别调整红、绿、蓝通道强度</small>
            </span>
            <button
              type="button"
              onClick={onResetColorBalance}
              disabled={
                switching ||
                (colorBalance.red === 100 &&
                  colorBalance.green === 100 &&
                  colorBalance.blue === 100)
              }
            >
              恢复默认
            </button>
          </div>
          {(
            [
              ['red', '红色', styles.colorSliderRed],
              ['green', '绿色', styles.colorSliderGreen],
              ['blue', '蓝色', styles.colorSliderBlue],
            ] as const
          ).map(([channel, label, sliderClass]) => (
            <label className={styles.colorSliderRow} key={channel}>
              <span>{label}</span>
              <input
                className={sliderClass}
                type="range"
                min={COLOR_BALANCE_MIN}
                max={COLOR_BALANCE_MAX}
                step={1}
                value={colorBalance[channel]}
                disabled={switching}
                onChange={(event) =>
                  onColorBalanceChange({
                    ...colorBalance,
                    [channel]: Number(event.currentTarget.value),
                  })
                }
                aria-label={`${label}通道强度`}
              />
              <output>{colorBalance[channel]}%</output>
            </label>
          ))}
        </div>
      </section>
    </div>
  );
}

function ShareDialog({ viewerLink, onClose }: { viewerLink: string; onClose: () => void }) {
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(viewerLink);
      setCopied(true);
    } catch {
      window.prompt('复制观看链接', viewerLink);
    }
  };

  return (
    <div
      className={styles.shareOverlay}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className={styles.shareDialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-dialog-title"
      >
        <button
          className={styles.shareCloseButton}
          type="button"
          onClick={onClose}
          aria-label="关闭分享浮窗"
          autoFocus
        >
          <CloseIcon />
        </button>
        <h2 id="share-dialog-title">分享直播间</h2>
        <p>扫码或复制链接，邀请朋友进入直播间</p>
        <div className={styles.shareQr} aria-label="观众进入链接二维码">
          <QRCodeSVG value={viewerLink} size={196} level="M" bgColor="#ffffff" fgColor="#0b0c10" />
        </div>
        <div className={styles.shareUrl} title={viewerLink}>
          {viewerLink}
        </div>
        <button className={styles.copyLinkButton} type="button" onClick={copyLink}>
          {copied ? '已复制链接' : '复制链接'}
        </button>
      </section>
    </div>
  );
}

function ParticipantListDialog({
  participants,
  hostIdentity,
  localIdentity,
  hostAvatarUrl,
  onClose,
}: {
  participants: Participant[];
  hostIdentity: string;
  localIdentity: string;
  hostAvatarUrl: string | null;
  onClose: () => void;
}) {
  const orderedParticipants = React.useMemo(
    () =>
      [...participants].sort((first, second) => {
        if (first.identity === hostIdentity) return -1;
        if (second.identity === hostIdentity) return 1;
        return (first.name || first.identity).localeCompare(
          second.name || second.identity,
          'zh-CN',
        );
      }),
    [hostIdentity, participants],
  );

  React.useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  return (
    <div
      className={styles.filterOverlay}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className={`${styles.filterDialog} ${styles.participantListDialog}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="participant-list-title"
      >
        <header className={styles.filterHeader}>
          <span>
            <strong id="participant-list-title">直播间用户</strong>
            <small>当前真实在线 {orderedParticipants.length} 人</small>
          </span>
          <button type="button" onClick={onClose} aria-label="关闭用户列表">
            <CloseIcon />
          </button>
        </header>
        <div className={styles.participantList}>
          {orderedParticipants.map((participant) => {
            const isHost = participant.identity === hostIdentity;
            const isLocal = participant.identity === localIdentity;
            const participantName = isHost ? '主播' : participant.name || '观众';
            return (
              <div className={styles.participantListItem} key={participant.identity}>
                <span
                  className={`${styles.participantAvatar} ${
                    isHost ? styles.participantAvatarHost : ''
                  }`}
                >
                  {isHost && hostAvatarUrl ? (
                    <NextImage
                      src={hostAvatarUrl}
                      alt="主播头像"
                      width={42}
                      height={42}
                      unoptimized
                      draggable={false}
                    />
                  ) : (
                    participantName.slice(0, 1)
                  )}
                </span>
                <span className={styles.participantDetails}>
                  <strong>{participantName}</strong>
                  <small>
                    <i /> 在线{isLocal ? ' · 我' : ''}
                  </small>
                </span>
                <span
                  className={`${styles.participantRole} ${
                    isHost ? styles.participantRoleHost : ''
                  }`}
                >
                  {isHost ? '主播' : '观众'}
                </span>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

type JoinNotice = {
  id: string;
  timestamp: number;
  name: string;
};

function LiveChat({ room, hostIdentity }: { room: Room; hostIdentity: string }) {
  const { chatMessages, send, isSending } = useChat();
  const [message, setMessage] = React.useState('');
  const [joinNotices, setJoinNotices] = React.useState<JoinNotice[]>([]);
  const listRef = React.useRef<HTMLDivElement>(null);
  const lastSentAt = React.useRef(0);

  const feedItems = React.useMemo(
    () =>
      [
        ...chatMessages.map((item) => ({
          kind: 'chat' as const,
          id: `chat-${item.id}`,
          timestamp: item.timestamp,
          item,
        })),
        ...joinNotices.map((notice) => ({
          kind: 'join' as const,
          id: notice.id,
          timestamp: notice.timestamp,
          notice,
        })),
      ]
        .sort((first, second) => first.timestamp - second.timestamp)
        .slice(-30),
    [chatMessages, joinNotices],
  );

  React.useEffect(() => {
    const announceParticipant = (participant: RemoteParticipant) => {
      if (participant.identity === hostIdentity) return;
      const timestamp = Date.now();
      setJoinNotices((current) => [
        ...current.slice(-29),
        {
          id: `join-${participant.sid}-${timestamp}`,
          timestamp,
          name: participant.name || '观众',
        },
      ]);
    };
    room.on(RoomEvent.ParticipantConnected, announceParticipant);
    return () => {
      room.off(RoomEvent.ParticipantConnected, announceParticipant);
    };
  }, [hostIdentity, room]);

  React.useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [feedItems.length]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = message.trim().slice(0, 500);
    if (!text || isSending || Date.now() - lastSentAt.current < 600) return;
    lastSentAt.current = Date.now();
    setMessage('');
    try {
      await send(text);
    } catch {
      setMessage(text);
    }
  };

  return (
    <div className={styles.chatArea}>
      <div className={styles.chatMessages} ref={listRef} aria-live="polite">
        {feedItems.length === 0 && (
          <div className={styles.welcomeMessage}>欢迎来到直播间，聊点什么吧</div>
        )}
        {feedItems.map((feedItem) => {
          if (feedItem.kind === 'join') {
            return (
              <div className={styles.joinMessage} key={feedItem.id}>
                <span className={styles.joinMessageIcon}>
                  <EnterIcon />
                </span>
                <span>
                  <strong>{feedItem.notice.name}</strong> 进入了直播间
                </span>
              </div>
            );
          }
          const isHost = feedItem.item.from?.identity === hostIdentity;
          return (
            <div className={styles.chatMessage} key={feedItem.id}>
              <span className={isHost ? styles.hostName : styles.viewerName}>
                {isHost ? '主播' : feedItem.item.from?.name || '观众'}
              </span>
              <span>{feedItem.item.message}</span>
            </div>
          );
        })}
      </div>
      <form className={styles.chatForm} onSubmit={submit}>
        <input
          value={message}
          onChange={(event) => setMessage(event.target.value.slice(0, 500))}
          placeholder="说点什么…"
          aria-label="发送聊天内容"
          maxLength={500}
          autoComplete="off"
        />
        <button type="submit" disabled={!message.trim() || isSending} aria-label="发送">
          <SendIcon />
        </button>
      </form>
    </div>
  );
}

function ControlButton({
  label,
  icon,
  active = false,
  onClick,
  disabled = false,
}: {
  label: string;
  icon: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      className={`${styles.controlButton} ${active ? styles.controlButtonActive : ''}`}
      type="button"
      onClick={onClick}
      disabled={disabled}
    >
      <span>{icon}</span>
      <small>{label}</small>
    </button>
  );
}

const CloseIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);
const MicIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Z" />
    <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3M9 21h6" />
  </svg>
);
const MicOffIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="m4 4 16 16M9 9v3a3 3 0 0 0 4.8 2.4M15 10V6a3 3 0 0 0-5.7-1.3M5.5 11.5A6.5 6.5 0 0 0 16 16.6M18.5 11.5a6.4 6.4 0 0 1-.5 2.5M12 18v3M9 21h6" />
  </svg>
);
const NoiseSuppressionIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M4 12h2l1.5-4 3 8L13 5l2.5 14 2-7H20" />
    <path d="M4 4v16M20 4v16" />
  </svg>
);
const CameraIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M4 7.5h10a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2ZM16 10l5-3v10l-5-3" />
  </svg>
);
const QualityIcon = () => (
  <svg viewBox="0 0 24 24">
    <rect x="3" y="5" width="18" height="13" rx="2" />
    <path d="M8 21h8M12 18v3M7 9h4v5M14 9h3v5h-3z" />
  </svg>
);
const FrameRateIcon = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7v5l3.5 2M4.5 5.5l2 2M19.5 5.5l-2 2" />
  </svg>
);
const SwitchCameraIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M7.5 7H15a3 3 0 0 1 3 3v1M16 4l2 3-3 2M16.5 17H9a3 3 0 0 1-3-3v-1M8 20l-2-3 3-2" />
    <circle cx="12" cy="12" r="2.5" />
  </svg>
);
const FilterIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M4 6.5h10M18 6.5h2M4 12h3M11 12h9M4 17.5h8M16 17.5h4" />
    <circle cx="16" cy="6.5" r="2" />
    <circle cx="9" cy="12" r="2" />
    <circle cx="14" cy="17.5" r="2" />
  </svg>
);
const BeautyIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 3.2c-3.9 0-6.7 3-6.7 7.1 0 4.5 2.8 8.5 6.7 10.5 3.9-2 6.7-6 6.7-10.5 0-4.1-2.8-7.1-6.7-7.1Z" />
    <path d="M9 10.5h.01M15 10.5h.01M9.5 15c1.5 1 3.5 1 5 0M18.5 3v3M17 4.5h3" />
  </svg>
);
const ViewerCountIcon = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="9" cy="8" r="3" />
    <path d="M3.5 18c.5-3.2 2.4-5 5.5-5s5 1.8 5.5 5M16 8.5a2.5 2.5 0 0 1 0 5M17 14c2.1.5 3.3 1.8 3.6 4" />
  </svg>
);
const VideoOffIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="m3 3 18 18M10 7.5h4a2 2 0 0 1 2 2v4M16 10l5-3v10l-3-1.8M13 16.5H4a2 2 0 0 1-2-2v-5c0-1 .7-1.8 1.6-2" />
  </svg>
);
const ShareIcon = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="18" cy="5" r="2.5" />
    <circle cx="6" cy="12" r="2.5" />
    <circle cx="18" cy="19" r="2.5" />
    <path d="m8.2 10.8 7.6-4.5M8.2 13.2l7.6 4.5" />
  </svg>
);
const SendIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="m4 4 17 8-17 8 3-8-3-8Z" />
    <path d="M7 12h14" />
  </svg>
);
const EnterIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M13 5h5a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5M10 8l4 4-4 4M14 12H4" />
  </svg>
);
